import { BeheerVos, useDeleteBeheerVos } from "@/entities/medewerkers-op-station/index.ts";
import {
    Badge,
    Button,
    ConfirmDialog,
    DataTable,
    DataTableColumnHeader,
    Icon,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/shared/components";
import type { DataTableFeatures } from "@/shared/components/table/data-table-features";
import { DataTableToolbarOptions } from "@/shared/components/table/data-table-toolbar";
import { getListPreview } from "@/shared/utils/list-preview.ts";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { UpsertVosDialog } from "./upsert-vos-dialog.tsx";

type VossenTableProps = {
    vossen: BeheerVos[];
};

export function VossenTable({ vossen }: VossenTableProps) {
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState<boolean>(false);
    const [selectedVos, setSelectedVos] = useState<BeheerVos | null>(null);
    const deleteVos = useDeleteBeheerVos();

    const columnHelper = createColumnHelper<DataTableFeatures, BeheerVos>();
    const columns = columnHelper.columns([
        columnHelper.accessor("naam", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Naam" />,
        }),
        columnHelper.accessor("email", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="E-mailadres" />,
        }),

        columnHelper.accessor("inzetStationnamen", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Stations" />,
            cell: ({ row }) => <StationsBadgeColumn stations={row.original.inzetStationnamen} />,
        }),
        columnHelper.accessor("isVosGroepLid", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="AD groep" />,
            cell: ({ row }) => (
                <Icon
                    className={row.original.isVosGroepLid ? "text-content-success" : "text-content-error"}
                    name={row.original.isVosGroepLid ? "check-circle" : "error"}
                />
            ),
        }),
        columnHelper.accessor("isActief", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Account actief" />,
            cell: ({ row }) => (
                <Icon
                    className={row.original.isActief ? "text-content-success" : "text-content-error"}
                    name={row.original.isActief ? "check-circle" : "error"}
                />
            ),
        }),
        columnHelper.accessor("mobielNummer", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Telefoon" />,
        }),
        columnHelper.display({
            id: "Acties",
            header: "Acties",
            cell: ({ row }) => (
                <div className="flex flex-row gap-2">
                    <Button
                        variant="tertiary"
                        iconOnly
                        icon="edit"
                        onClick={() => {
                            setDialogOpen(true);
                            setSelectedVos(row.original);
                        }}
                    />
                    <Button
                        variant="tertiary"
                        iconOnly
                        icon="trash"
                        onClick={() => {
                            setSelectedVos(row.original);
                            setConfirmDeleteDialogOpen(true);
                        }}
                    />
                </div>
            ),
        }),
    ]);

    const toolbarOptions: DataTableToolbarOptions<BeheerVos> = {
        filters: {
            globalFilter: {
                columns: ["naam", "email", "inzetStationnamen"],
                placeholder: "Zoek op naam, station of e-mail...",
            },
        },
        actions: [
            {
                label: "Nieuwe vrijwilliger registreren",
                onClick: () => {
                    setDialogOpen(true);
                },
                "data-testid": "nieuwe-vos-registreren",
            },
        ],
    };

    const onOpenChange = (open: boolean) => {
        if (!open) setSelectedVos(null);
        setDialogOpen(open);
    };

    const handleDeleteVos = (vos: BeheerVos) => {
        deleteVos.mutate(
            { entraId: vos.entraId },
            {
                onSuccess: () => {
                    setConfirmDeleteDialogOpen(false);
                    setSelectedVos(null);
                },
            }
        );
    };

    return (
        <div>
            <DataTable
                columns={columns}
                data={vossen}
                toolbarOptions={toolbarOptions}
                onDoubleClick={(vos) => {
                    setSelectedVos(vos.original);
                    setDialogOpen(true);
                }}
            />
            <UpsertVosDialog key={`upsert-vos-${selectedVos?.entraId}`} vos={selectedVos} open={dialogOpen} onOpenChange={onOpenChange} />
            {selectedVos && (
                <ConfirmDialog
                    destructive
                    key={`confirm-delete-vos-${selectedVos.entraId}`}
                    isOpen={confirmDeleteDialogOpen}
                    onCancel={() => setConfirmDeleteDialogOpen(false)}
                    onConfirm={() => handleDeleteVos(selectedVos)}
                    isPending={deleteVos.isPending}
                    error={deleteVos.error}
                    title="Vrijwilliger verwijderen"
                    confirmLabel="Vrijwilliger verwijderen"
                >
                    Weet je zeker dat je deze vos wil verwijderen?
                </ConfirmDialog>
            )}
        </div>
    );
}

type StationsBadgeColumnProps = {
    stations: string[];
};

const StationsBadgeColumn = ({ stations }: StationsBadgeColumnProps) => {
    const { remainder, previewItems } = getListPreview(stations, 3);

    return (
        <div className="ma flex flex-row gap-1">
            {previewItems.map((naam) => (
                <Badge key={`badge-${naam}`} variant="gray-subtle">
                    {naam}
                </Badge>
            ))}
            {remainder > 0 && (
                <Tooltip>
                    <TooltipTrigger>
                        <Badge variant="gray-subtle">+{remainder}</Badge>
                    </TooltipTrigger>
                    <TooltipContent className="whitespace-pre-line">{stations.join("\n")}</TooltipContent>
                </Tooltip>
            )}
        </div>
    );
};
